/**
 * Australian Date Formatting Utilities
 * Format: DD/MM/YYYY (Australian standard)
 */

/**
 * Format date to Australian format DD/MM/YYYY
 * @param {string|Date} dateInput - Date string or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDateAU = (dateInput, options = {}) => {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) return '';
    
    const { includeTime = false, shortMonth = false } = options;
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    if (shortMonth) {
      const monthName = date.toLocaleDateString('en-AU', { month: 'short' });
      return `${day} ${monthName} ${year}`;
    }
    
    let formatted = `${day}/${month}/${year}`;
    
    if (includeTime) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      formatted += ` ${hours}:${minutes}`;
    }
    
    return formatted;
  } catch (e) {
    console.error('Date formatting error:', e);
    return '';
  }
};

/**
 * Format date with month name (e.g., "26 February 2025")
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateLongAU = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) return '';
    
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch (e) {
    console.error('Date formatting error:', e);
    return '';
  }
};

/**
 * Format date for display in lists (e.g., "26 Feb 2025")
 * @param {string|Date} dateInput - Date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateShortAU = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) return '';
    
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch (e) {
    console.error('Date formatting error:', e);
    return '';
  }
};

/**
 * Format month and year (e.g., "February 2025")
 * @param {string} monthYear - Month string in format "YYYY-MM"
 * @returns {string} Formatted month/year string
 */
export const formatMonthYearAU = (monthYear) => {
  if (!monthYear) return '';
  
  try {
    const [year, month] = monthYear.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  } catch (e) {
    console.error('Date formatting error:', e);
    return '';
  }
};

export default {
  formatDateAU,
  formatDateLongAU,
  formatDateShortAU,
  formatMonthYearAU
};
